import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  SiteLayout, PageHero, SectionTag, WhatsAppIcon, waLink, submitLead,
  PHONE_PRIMARY, PHONE_PRIMARY_TEL, PHONE_ALT_2, COMPANY_EMAIL,
  COMPANY_ADDRESS, BUSINESS_HOURS, MAP_EMBED_SRC, SERVICES, useSEO,
} from "./shared";

const TYPES = [
  { v: "contact", label: "General Enquiry" },
  { v: "quote", label: "Get a Quote" },
  { v: "site_visit", label: "Free Site Visit" },
];

const EMPTY = { name: "", organization: "", phone: "", email: "", building_type: "", service: "", location: "", preferred_date: "", message: "" };

export default function Contact() {
  useSEO(
    "Contact SecureFlame Fire Systems | Fire Protection Company in Tripura",
    "Get in touch with SecureFlame Fire Systems Pvt. Ltd. — call, WhatsApp or send an enquiry for fire alarm, hydrant, sprinkler, pump, extinguisher installation and AMC services across Tripura."
  );
  const [params] = useSearchParams();
  const [type, setType] = useState("contact");
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = params.get("type");
    if (t && TYPES.some((x) => x.v === t)) setType(t);
    const service = params.get("service");
    const subject = params.get("subject");
    setForm((f) => ({
      ...f,
      service: service ? (SERVICES.find((s) => s.slug === service)?.title || service) : f.service,
      message: subject === "credentials"
        ? "I would like to request SecureFlame's company credentials, registration details and technical information."
        : f.message,
    }));
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || done) return;
    if (!form.name.trim() || !form.phone.trim()) { setError("Please enter your name and phone number."); return; }
    if (type === "site_visit" && !form.location.trim()) { setError("Please enter the site address for a free site visit."); return; }
    setSubmitting(true); setError("");
    const res = await submitLead(type, { ...form, site_address: form.location });
    setSubmitting(false);
    if (res.ok) { setDone(true); setForm(EMPTY); } else setError(res.error);
  };

  return (
    <SiteLayout>
      <PageHero eyebrow="Get In Touch" title="Let's Discuss Your Fire Safety Requirements" subtitle="Call, WhatsApp, or send an enquiry below — our team will respond and arrange a free site visit where required." />

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-5 gap-12">
        {/* Contact info */}
        <div className="lg:col-span-2 space-y-8">
          <div>
            <SectionTag>SecureFlame Fire Systems Private Limited</SectionTag>
            <div className="space-y-4 text-sm text-secondary mt-4">
              <p className="flex items-start gap-3"><MapPin size={18} className="text-primary shrink-0 mt-0.5" /> {COMPANY_ADDRESS}</p>
              <a href={`tel:${PHONE_PRIMARY_TEL}`} className="flex items-start gap-3 hover:text-primary" data-testid="contact-phone-primary"><Phone size={18} className="text-primary shrink-0 mt-0.5" /> Primary: {PHONE_PRIMARY}</a>
              <p className="flex items-start gap-3 text-secondary/80"><Phone size={18} className="text-transparent shrink-0 mt-0.5" /> Alternate: {PHONE_ALT_2}</p>
              <a href={`mailto:${COMPANY_EMAIL}`} className="flex items-start gap-3 hover:text-primary" data-testid="contact-email"><Mail size={18} className="text-primary shrink-0 mt-0.5" /> {COMPANY_EMAIL}</a>
              <p className="flex items-start gap-3"><Clock size={18} className="text-primary shrink-0 mt-0.5" /> <span>{BUSINESS_HOURS.map((b) => `${b.d}: ${b.h}`).join(" · ")}</span></p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={`tel:${PHONE_PRIMARY_TEL}`}><Button size="lg" className="rounded-sm bg-primary hover:bg-primary/90 gap-2" data-testid="contact-call-btn"><Phone size={17} /> CALL NOW</Button></a>
            <a href={waLink()} target="_blank" rel="noreferrer"><Button size="lg" className="rounded-sm bg-[#128C4A] hover:bg-[#0f7a3f] gap-2" data-testid="contact-whatsapp-btn"><WhatsAppIcon size={17} /> WHATSAPP</Button></a>
          </div>
          <div className="rounded-sm overflow-hidden border border-border h-56 lg:h-64">
            <iframe title="SecureFlame Fire Systems location map" src={MAP_EMBED_SRC} className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-3 bg-card border border-border rounded-sm p-6 sm:p-8">
          {done ? (
            <div className="text-center py-14" data-testid="contact-success-message">
              <CheckCircle2 className="text-primary mx-auto mb-4" size={44} />
              <h3 className="font-heading font-bold text-2xl text-secondary">Thank you — we've received your enquiry.</h3>
              <p className="mt-2 text-muted-foreground">Our team will get back to you shortly. For urgent requirements, please call or WhatsApp us directly.</p>
              <Button onClick={() => setDone(false)} variant="outline" className="mt-6 rounded-sm" data-testid="contact-send-another-btn">Send Another Enquiry</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="flex flex-wrap gap-2" data-testid="contact-type-tabs">
                {TYPES.map((t) => (
                  <button key={t.v} type="button" onClick={() => setType(t.v)}
                    className={`text-xs sm:text-sm font-semibold px-4 py-2 rounded-full border transition-colors ${type === t.v ? "bg-primary text-white border-primary" : "border-border text-secondary hover:border-primary/40"}`}
                    data-testid={`contact-type-${t.v}`}>{t.label}</button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label htmlFor="c-name">Full Name *</Label><Input id="c-name" value={form.name} onChange={set("name")} className="mt-1.5 rounded-sm" data-testid="contact-input-name" /></div>
                <div><Label htmlFor="c-org">Organization</Label><Input id="c-org" value={form.organization} onChange={set("organization")} className="mt-1.5 rounded-sm" data-testid="contact-input-organization" /></div>
                <div><Label htmlFor="c-phone">Phone *</Label><Input id="c-phone" type="tel" value={form.phone} onChange={set("phone")} className="mt-1.5 rounded-sm" data-testid="contact-input-phone" /></div>
                <div><Label htmlFor="c-email">Email</Label><Input id="c-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5 rounded-sm" data-testid="contact-input-email" /></div>
                <div><Label htmlFor="c-building">Building / Project Type</Label><Input id="c-building" placeholder="e.g. Hospital, Apartment, Office" value={form.building_type} onChange={set("building_type")} className="mt-1.5 rounded-sm" data-testid="contact-input-building-type" /></div>
                <div><Label htmlFor="c-service">Service Required</Label><Input id="c-service" placeholder="e.g. Fire Alarm, AMC" value={form.service} onChange={set("service")} className="mt-1.5 rounded-sm" data-testid="contact-input-service" /></div>
                <div><Label htmlFor="c-location">{type === "site_visit" ? "Site Address *" : "Location"}</Label><Input id="c-location" value={form.location} onChange={set("location")} className="mt-1.5 rounded-sm" data-testid="contact-input-location" /></div>
                {type === "site_visit" && (
                  <div><Label htmlFor="c-date">Preferred Date</Label><Input id="c-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} className="mt-1.5 rounded-sm" data-testid="contact-input-preferred-date" /></div>
                )}
              </div>
              <div><Label htmlFor="c-message">Message</Label><Textarea id="c-message" rows={4} value={form.message} onChange={set("message")} className="mt-1.5 rounded-sm" data-testid="contact-input-message" /></div>
              {error && <p className="text-sm text-primary" data-testid="contact-error-message">{error}</p>}
              <Button type="submit" size="lg" disabled={submitting} className="rounded-sm bg-primary hover:bg-primary/90 font-semibold gap-2 w-full sm:w-auto" data-testid="contact-submit-btn">
                {submitting ? <><Loader2 size={17} className="animate-spin" /> Sending…</> : "SEND ENQUIRY"}
              </Button>
            </form>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
